"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { api, Case, SubscriptionStatus } from "@/lib/api";
import { CaseCard } from "@/components/CaseCard";
import { AppShell } from "@/components/AppShell";
import { PageHelp } from "@/components/PageHelp";
import { TesterKeyModal } from "@/components/TesterKeyModal";
import { getToken } from "@/lib/auth";

type MeData = { id: number; name: string; email: string; has_api_key: boolean; consent_accepted: boolean };

function TrialBanner({ status }: { status: SubscriptionStatus }) {
  const t = useTranslations("dashboard.trial");
  if (status.tier !== "trial") return null;
  const sessionsLeft = status.trial_sessions_remaining ?? 0;
  const daysLeft = status.days_remaining ?? 0;
  const isAlmostDone = sessionsLeft <= 1 || daysLeft <= 3;

  return (
    <div
      className="mb-6 flex items-center justify-between rounded-xl px-4 py-3 text-sm"
      style={isAlmostDone
        ? { background: 'var(--accent-warm-pale)', border: '1px solid rgba(200,135,58,0.25)', color: 'var(--accent-warm)' }
        : { background: 'var(--accent-light)', border: '1px solid rgba(15,76,76,0.12)', color: 'var(--accent)' }
      }
    >
      <span>
        <span className="font-semibold">{t("active")}</span> —{" "}
        {t("remaining", { sessionsLeft, daysLeft })}
      </span>
      <button
        onClick={() => alert(t("paymentSoon"))}
        className="ml-4 shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
        style={{ background: isAlmostDone ? 'var(--accent-warm)' : 'var(--accent)' }}
      >
        {t("upgradeButton")}
      </button>
    </div>
  );
}

// Free tier is limited (not blocked): show a banner with the monthly session
// allowance + an upgrade link to the pricing page.
function FreeBanner({ status }: { status: SubscriptionStatus }) {
  const t = useTranslations("dashboard.free");
  const used = status.sessions_used_this_month ?? 0;
  const limit = status.sessions_limit ?? 5;
  return (
    <div
      className="mb-6 flex items-center justify-between rounded-xl px-4 py-3 text-sm"
      style={{ background: 'var(--accent-light)', border: '1px solid rgba(15,76,76,0.12)', color: 'var(--accent)' }}
    >
      <span>
        <span className="font-semibold">{t("banner")}</span> — {t("used", { used, limit })}
      </span>
      <a
        href="/pricing"
        className="ml-4 shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
        style={{ background: 'var(--accent)' }}
      >
        {t("upgrade")}
      </a>
    </div>
  );
}

export default function Dashboard() {
  const tHeader = useTranslations("dashboard.header");
  const tCustom = useTranslations("dashboard.customPatients");
  const tView = useTranslations("dashboard.view");

  const [cases, setCases] = useState<Case[]>([]);
  const [sessionCounts, setSessionCounts] = useState<Record<number, number>>({});
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [me, setMe] = useState<MeData | null>(null);
  const [view, setView] = useState<"card" | "list">("card");
  // Pending action to resume after the user supplies a BYO key inline:
  // either start a session for a case, or open the create-patient form.
  const [testerKeyModal, setTesterKeyModal] = useState<{ type: "session"; case: Case } | { type: "create" } | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!getToken()) { router.push("/login"); return; }

    Promise.all([
      api.getCases(),
      api.getSubscriptionStatus(),
      api.getMe(),
    ]).then(([loaded, status, meData]) => {
      // KVKK + Açık Rıza onayı verilmemişse (yarım kalmış kayıt) uygulamaya girilemez.
      if (!meData.consent_accepted) { router.replace("/consent"); return; }
      // Hastaları isme göre alfabetik (TR) sırala — Aylin, Emre, Zeynep …
      setCases([...loaded].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setSubStatus(status);
      setMe(meData);
      Promise.all(loaded.map((c) => api.getCaseState(c.id).then((s) => ({ id: c.id, count: s.session_count }))))
        .then((results) => {
          const map: Record<number, number> = {};
          results.forEach(({ id, count }) => { map[id] = count; });
          setSessionCounts(map);
        });
    }).catch(() => router.push("/login"));
  }, [router]);

  useEffect(() => {
    const saved = localStorage.getItem("pp_cases_view");
    if (saved === "list" || saved === "card") setView(saved);
  }, []);

  function changeView(v: "card" | "list") {
    setView(v);
    localStorage.setItem("pp_cases_view", v);
  }

  async function proceedStart(c: Case) {
    const session = await api.startSession(c.id);
    router.push(`/session/${session.id}?patient=${encodeURIComponent(c.name)}&age=${c.age}`);
  }

  async function handleStart(c: Case) {
    // Tester AND trial tiers never use the server's API key — they must bring
    // their own. Prompt for it inline (key modal) instead of failing the
    // request, so a brand-new trial user isn't dead-ended into /pricing.
    if ((subStatus?.tier === "tester" || subStatus?.tier === "trial") && !me?.has_api_key) {
      setTesterKeyModal({ type: "session", case: c });
      return;
    }
    try {
      await proceedStart(c);
    } catch (e) {
      let msg = e instanceof Error ? e.message : "";
      let code = "";
      try {
        const parsed = JSON.parse(msg);
        // detail may be a string (free 402) or an object {code, message} (428).
        code = parsed?.detail?.code ?? "";
        msg = parsed?.detail?.message ?? parsed?.detail ?? msg;
      } catch { /* plain text */ }
      // A "bring your own key" 428 should open the key modal, not push to
      // pricing — buying a plan doesn't satisfy a BYO-key requirement.
      if (code === "api_key_required") {
        setTesterKeyModal({ type: "session", case: c });
        return;
      }
      // Otherwise (e.g. free monthly allowance used up → 402) surface the
      // message and route to pricing so the user can upgrade.
      alert(msg);
      router.push("/pricing");
    }
  }

  function handleCreatePatient() {
    // Same BYO-key gate as starting a session: a tester/trial user without a key
    // gets the inline key modal immediately, instead of landing on the create
    // form only to fail on submit.
    if ((subStatus?.tier === "tester" || subStatus?.tier === "trial") && !me?.has_api_key) {
      setTesterKeyModal({ type: "create" });
      return;
    }
    router.push("/create-patient");
  }

  const isFree = subStatus?.tier === "free";

  return (
    <AppShell active="cases">
      {testerKeyModal && (
        <TesterKeyModal
          onClose={() => setTesterKeyModal(null)}
          onSaved={async () => {
            const pending = testerKeyModal;
            setMe((prev) => (prev ? { ...prev, has_api_key: true } : prev));
            setTesterKeyModal(null);
            if (pending?.type === "session") {
              await proceedStart(pending.case);
            } else if (pending?.type === "create") {
              router.push("/create-patient");
            }
          }}
        />
      )}

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
              {tHeader("title")}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">{tHeader("subtitle")}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {subStatus && (
              <div className="flex items-center gap-1 p-1 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]">
              <button
                onClick={() => changeView("card")}
                title={tView("card")}
                aria-label={tView("card")}
                className={`p-1.5 rounded-lg transition-colors ${view === "card" ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
                </svg>
              </button>
              <button
                onClick={() => changeView("list")}
                title={tView("list")}
                aria-label={tView("list")}
                className={`p-1.5 rounded-lg transition-colors ${view === "list" ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              </button>
              </div>
            )}
            <PageHelp topic="dashboard" />
          </div>
        </div>

        {subStatus && <TrialBanner status={subStatus} />}
        {isFree && subStatus && <FreeBanner status={subStatus} />}

        <div className={view === "list" ? "flex flex-col gap-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
          {cases.map((c) => (
            <CaseCard key={c.id} c={c} sessionCount={sessionCounts[c.id] ?? 0} onStart={() => handleStart(c)} view={view} />
          ))}
        </div>

        {(subStatus?.tier === "pro" || subStatus?.tier === "tester") && (
          <div className="mt-8 border-t border-[var(--border)] pt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-[var(--text-primary)]">{tCustom("title")}</h2>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{tCustom("description")}</p>
              </div>
              <button
                type="button"
                onClick={handleCreatePatient}
                className="inline-flex items-center gap-2 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors"
                style={{ background: 'var(--accent)' }}
                onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
              >
                {tCustom("newButton")}
              </button>
            </div>
            {cases.filter(c => c.is_custom).length === 0 && (
              <p className="text-sm text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-xl p-6 text-center">
                {tCustom("empty")}
              </p>
            )}
          </div>
        )}
      </main>
    </AppShell>
  );
}
